import { useEffect } from 'react';
import { Link } from 'react-router';
import {
    Cloud,
    Monitor,
    Users,
    Circle,
    ArrowRight,
    Database,
    RefreshCw,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageTitle } from '@/hooks/use-page-title';
import { useHeaderAction } from '@/lib/header-action-context';
import {
    useFederationStatus,
    type RegistrySyncInfo,
} from '@/hooks/use-federation-status';

const kindIcons: Record<string, typeof Cloud> = {
    local: Monitor,
    cloud: Cloud,
    partner: Users,
};

const statusConfig: Record<string, { dot: string; label: string }> = {
    active: { dot: 'text-emerald-500', label: 'Online' },
    disabled: { dot: 'text-slate-400', label: 'Disabled' },
    unreachable: { dot: 'text-amber-500', label: 'Unreachable' },
};

function formatTimeAgo(isoString?: string): string {
    if (!isoString) return 'Never';
    const diff = Date.now() - new Date(isoString).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

function RegistrySyncCard({ info }: { info: RegistrySyncInfo }) {
    const Icon = kindIcons[info.kind] ?? Cloud;
    const status = statusConfig[info.status] ?? statusConfig.disabled;
    const displayName = info.display_name ?? info.alias;

    return (
        <Card>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Circle size={8} className={`fill-current ${status.dot}`} />
                        <Icon size={16} className="text-muted-foreground" />
                        <CardTitle className="text-base">{displayName}</CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                        {info.supports_federation && (
                            <Badge variant="outline" className="text-xs">
                                <RefreshCw size={10} className="mr-1" />
                                Federation
                            </Badge>
                        )}
                        <Badge
                            variant="outline"
                            className={`text-xs ${info.status === 'active' ? 'border-emerald-500/30 text-emerald-600' : info.status === 'unreachable' ? 'border-amber-500/30 text-amber-600' : ''}`}
                        >
                            {status.label}
                        </Badge>
                    </div>
                </div>
                <CardDescription>
                    {info.alias} &middot; Last seen: {formatTimeAgo(info.last_seen_at)}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-3 gap-4">
                    <div className="rounded-lg border p-3">
                        <div className="text-2xl font-bold">{info.card_counts.authoritative}</div>
                        <div className="text-xs text-muted-foreground">Authoritative</div>
                    </div>
                    <div className="rounded-lg border p-3">
                        <div className="text-2xl font-bold">{info.card_counts.federated}</div>
                        <div className="text-xs text-muted-foreground">Federated</div>
                    </div>
                    <div className="rounded-lg border p-3">
                        <div className="text-2xl font-bold">{info.card_counts.total}</div>
                        <div className="text-xs text-muted-foreground">Total</div>
                    </div>
                </div>
                <div className="mt-3 flex justify-end">
                    <Link
                        to={`/registries/${info.registry_id}`}
                        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                    >
                        View details <ArrowRight size={12} />
                    </Link>
                </div>
            </CardContent>
        </Card>
    );
}

export default function FederationStatusPage() {
    const { data, isLoading, error } = useFederationStatus();
    const { setHeaderTitle } = useHeaderAction();

    usePageTitle('Federation Status');

    useEffect(() => {
        setHeaderTitle({ title: 'Federation Status' });
        return () => setHeaderTitle(null);
    }, [setHeaderTitle]);

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-8 w-48 rounded bg-muted" />
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-48 rounded-lg bg-muted" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
                    Failed to load federation status: {String(error)}
                </div>
            </div>
        );
    }

    const registries = data?.registries ?? [];
    const totalCards = data?.total_cards ?? 0;
    const fedRegistries = registries.filter((r) => r.supports_federation);
    const onlineCount = registries.filter((r) => r.status === 'active').length;

    return (
        <div className="space-y-6 p-6">
            <div>
                <p className="text-muted-foreground">
                    Overview of card synchronization across connected registries.
                    Auto-refreshes every 30 seconds.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="rounded-lg bg-blue-100 p-2 text-blue-600">
                                <Database size={20} />
                            </div>
                            <div>
                                <div className="text-2xl font-bold">{totalCards}</div>
                                <div className="text-xs text-muted-foreground">Total Cards</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="rounded-lg bg-emerald-100 p-2 text-emerald-600">
                                <Monitor size={20} />
                            </div>
                            <div>
                                <div className="text-2xl font-bold">{registries.length}</div>
                                <div className="text-xs text-muted-foreground">Registries</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="rounded-lg bg-purple-100 p-2 text-purple-600">
                                <RefreshCw size={20} />
                            </div>
                            <div>
                                <div className="text-2xl font-bold">{fedRegistries.length}</div>
                                <div className="text-xs text-muted-foreground">
                                    Federation-enabled
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="rounded-lg bg-amber-100 p-2 text-amber-600">
                                <Circle size={20} className="fill-current" />
                            </div>
                            <div>
                                <div className="text-2xl font-bold">
                                    {onlineCount}/{registries.length}
                                </div>
                                <div className="text-xs text-muted-foreground">Online</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {registries.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center">
                        <p className="text-muted-foreground">
                            No registries configured.{' '}
                            <Link to="/registries" className="underline">
                                Add a registry
                            </Link>{' '}
                            to get started with federation.
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {registries.map((info) => (
                        <RegistrySyncCard key={info.registry_id} info={info} />
                    ))}
                </div>
            )}
        </div>
    );
}
